import { GetBusinessInfoResponse } from "@1games-ru/region-shared/dist/backend/requests/business";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { CaseIcon, IdIcon } from "@/shared/icons";
import { Button, Location, Modal } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";
import BusinessBuyModal from "@/domains/business/components/BusinessBuyModal";
import BusinessModalTitle from "@/domains/business/components/modals/BusinessModalTitle";
import BusinessModalWiew from "@/domains/business/components/modals/BusinessModalWiew";

type Props = {
  isLoading: boolean;
  info: GetBusinessInfoResponse;
  exit: () => void;
  buyBusiness: (paymentType: PaymentType, cb: () => void) => void;
  isBuying: boolean;
};

export default function BusinessSaleModalContainer({
  isLoading,
  info,
  exit,
  buyBusiness,
  isBuying,
}: Props) {
  const { t } = useTranslation("business");

  const [isBuyModal, setIsBuyModal] = useState(false);
  const [overlayClass, setOverlayClass] = useState("");

  const title = !isLoading ? (
    <BusinessModalTitle name={info?.name} level={t(`business.level.${info?.level}`)} />
  ) : (
    ""
  );

  const footer = (
    <Button className="h-11" onClick={() => setIsBuyModal(true)}>
      <CaseIcon className="mr-2.5 size-4.5" />
      {t("business.buyBusiness")}
    </Button>
  );

  const logo = info?.id ? getBusinessImage(info?.id, "logo") : "";
  const address = info?.address ? `${info?.address?.streetName} ${info?.address?.index}` : "";

  const onBuyBusinessSuccess = useCallback(
    (type: PaymentType) => {
      buyBusiness(type, () => setIsBuyModal(false));
    },
    [buyBusiness],
  );

  useEffect(() => {
    if (isBuyModal) {
      setOverlayClass("pointer-events-none");
    } else {
      const timer = setTimeout(() => {
        setOverlayClass("pointer-events-auto");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isBuyModal]);

  return (
    <>
      {isBuyModal && (
        <BusinessBuyModal
          name={info?.name}
          address={address}
          price={info?.price}
          onClose={() => setIsBuyModal(false)}
          onConfirm={onBuyBusinessSuccess}
          isLoading={isBuying}
          isConfirming={isBuying}
        />
      )}
      <Modal
        open
        title={title}
        footer={footer}
        onClose={exit}
        className="w-135 h-[38.5rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
        classNameCloseBtn="top-6.5 right-7.5"
        classNameOverlay={overlayClass}
        dataMzAccent="lightBlue"
        style={{
          fontFamily: "TT Norms Pro",
          boxShadow: "0px 0px 200px 0px rgba(0, 121, 194, 0.30)",
        }}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <BusinessModalWiew id={info?.id} isAvita={info?.isOnMarketplace} isSale />
            <div className="mb-2 flex items-center px-1">
              <CaseIcon className="size-4.5 text-accent-solid" />
              <p className="ml-2 text-14 font-bold text-accent-solid leading-none uppercase">
                {t("business.forSale")}
              </p>
              <div
                className="relative flex w-[6.6875rem] h-6 ml-auto"
                style={{
                  backgroundImage: `url(${logo})`,
                  backgroundPosition: "top right",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                }}
              />
            </div>
            <Location address={address} />
            <div className="flex items-center gap-2.5 mt-3 px-1 pb-4">
              <IdIcon className="size-4.5 text-white/60" />
              <div className="flex gap-1 text-16 font-medium leading-none">
                <p className="text-white/60">{t("business.id")}:</p>
                <p>#{info?.id}</p>
              </div>
            </div>
            <div className="flex justify-between items-center p-3.5 border-t border-t-[#191919]">
              <p className="text-16 font-medium leading-none">{t("business.stateCost")}:</p>
              <p className="text-16 font-medium leading-none">{formatPrice(info?.price)} ₽</p>
            </div>
            <div className="flex justify-between items-center p-3.5 border-t border-t-[#191919]">
              <p className="text-16 font-medium leading-none">{t("business.feePerDay")}:</p>
              <p className="text-16 font-medium leading-none">{formatPrice(info?.baseFee)} ₽</p>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
